import { getAttributeFeedback } from 'Services/exams.service'
import { ContextHandlerConfigType } from './IContextHandler'
import { AttributeFeedbackType, ExamPreviewConfigType } from 'Types/Exams.types'
import { forEach, isEmpty } from 'lodash'
import { ContainerType, TaskMode } from 'Types/Tasks.types'

export class FeedbackManager<T extends ContainerType, U = unknown> {
    private config: ContextHandlerConfigType<T, U>
    private feedback: AttributeFeedbackType[] | undefined
    private fetchingFeedback = false

    constructor(config: ContextHandlerConfigType<T, U>) {
        this.config = config
        this.initializeFeedback()
    }

    initializeFeedback = () => {
        const taskConfig = this.config.getStudio().studioConfig.taskConfig as ExamPreviewConfigType
        if (taskConfig.mode === TaskMode.Viewer) {
            const attribute_evaluations = taskConfig?.evaluationInfo?.attribute_evaluations
            const attributeFeedbacks: AttributeFeedbackType[] = []
            forEach(attribute_evaluations, attribute_evaluation => {
                attributeFeedbacks.push({
                    id: attribute_evaluation.attribute_id,
                    is_correct: true,
                    feedback: attribute_evaluation.feedbacks_info?.correct_value_feedback ?? '',
                    correct_answer: attribute_evaluation.correct_value
                        ?.value as AttributeFeedbackType['correct_answer'],
                })
                if (this.isTrainingMode()) {
                    attributeFeedbacks.push({
                        id: attribute_evaluation.attribute_id,
                        is_correct: false,
                        feedback:
                            attribute_evaluation.feedbacks_info?.incorrect_value_feedback ?? '',
                        correct_answer: attribute_evaluation.correct_value
                            ?.value as AttributeFeedbackType['correct_answer'],
                    })
                }
            })
            if (!isEmpty(attributeFeedbacks)) {
                this.feedback = attributeFeedbacks
            }
        }
    }

    private isTrainingMode = () => {
        return this.config.getStudio().studioConfig.context.metaData?.isTrainingMode
    }

    public fetchFeedback = async () => {
        const answerId = this.config.getStudio().studioConfig.context.metaData?.examInfo
            ?.answerId as string
        if (!answerId) {
            throw new Error('No answer id found')
        }
        try {
            const feedback = await getAttributeFeedback(answerId)
            this.feedback = feedback.result.map(feedback => {
                const transformedFeedback: AttributeFeedbackType = {
                    id: feedback.attribute_version_id,
                    correct_answer: feedback.correct_answer,
                    is_correct: feedback.is_correct,
                    feedback: feedback.feedback,
                }
                return transformedFeedback
            })
        } catch (error) {
            console.error('Error getting attribute feedback', error)
        } finally {
            this.fetchingFeedback = false
        }
        return this.feedback
    }

    public getFeedback = () => {
        return this.feedback
    }

    public isFetchingFeedback = () => {
        return this.fetchingFeedback
    }

    public isFeedbackFetched = () => {
        return this.feedback !== undefined
    }
}
